import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CartItem } from './CartItem';
import { CartSummary } from './CartSummary';
import useCartStore from '@/store/cartStore';
import { ShoppingBag, ArrowRight } from 'lucide-react';

export function CartDrawer({ open, onOpenChange }) {
  const navigate = useNavigate();
  const items = useCartStore((state) => state.items);
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const removeItem = useCartStore((state) => state.removeItem);
  const clearCart = useCartStore((state) => state.clearCart);
  const getCartSummary = useCartStore((state) => state.getCartSummary);
  const isEmpty = useCartStore((state) => state.isEmpty);

  const summary = getCartSummary();

  const handleCheckout = () => {
    onOpenChange(false);
    navigate('/checkout');
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            購物車
          </SheetTitle>
          <SheetDescription>
            {isEmpty() ? '購物車是空的' : `共 ${summary.totalItems} 件商品`}
          </SheetDescription>
        </SheetHeader>

        {isEmpty() ? (
          /* 空購物車狀態 */
          <div className="flex-1 flex flex-col items-center justify-center text-center py-12">
            <ShoppingBag className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              購物車是空的
            </h3>
            <p className="text-gray-500 mb-6">
              快去挑選喜歡的商品吧！
            </p>
            <Button onClick={() => {
              onOpenChange(false);
              navigate('/product');
            }}>
              前往選購
            </Button>
          </div>
        ) : (
          /* 有商品時的購物車內容 */
          <>
            {/* 商品列表 - 可滾動區域 */}
            <div className="flex-1 overflow-y-auto -mx-6 px-6">
              <div className="space-y-2">
                {items.map((item) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    onUpdateQuantity={updateQuantity}
                    onRemove={removeItem}
                  />
                ))}
              </div>
            </div>

            <Separator className="my-4" />

            {/* 價格摘要 */}
            <div className="space-y-4">
              <CartSummary summary={summary} />
              
              {/* 團購優惠說明 */}
              {summary.bulkDiscountInfo.applicable && (
                <Alert className="border-green-200 bg-green-50">
                  <AlertDescription className="text-green-800 text-sm">
                    🎉 您已享有 {summary.bulkDiscountInfo.message}，
                    省下 {summary.bulkDiscountAmount.toLocaleString()} 元！
                  </AlertDescription>
                </Alert>
              )}

              {/* 操作按鈕 */}
              <div className="space-y-2">
                <Button 
                  className="w-full h-12 text-lg" 
                  onClick={handleCheckout}
                >
                  前往結帳
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                
                <Button 
                  variant="ghost" 
                  className="w-full" 
                  onClick={clearCart}
                >
                  清空購物車
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
